import React from "react";
import { ExternalLink, UserPlus } from "lucide-react";
import type { CopyTradeLeader } from "@/api/copy-trade";

interface CopyTradeLeaderCardProps {
  leader: CopyTradeLeader;
  isSubscribed?: boolean;
  /** Position in the browse list (by the leaders API order). */
  rank?: number;
  onViewDetails: () => void;
  onSubscribe: () => void;
}

const isFiniteNumber = (value: number | null | undefined): value is number =>
  value != null && Number.isFinite(value);

const formatPercent = (value: number | null | undefined) =>
  isFiniteNumber(value) ? `${value.toFixed(2)}%` : "—";

const formatNumber = (value: number | null | undefined) =>
  isFiniteNumber(value)
    ? value.toLocaleString(undefined, { maximumFractionDigits: 2 })
    : "—";

export const CopyTradeLeaderCard: React.FC<CopyTradeLeaderCardProps> = ({
  leader,
  isSubscribed,
  rank,
  onViewDetails,
  onSubscribe,
}) => {
  return (
    <div
      className={`glass flex flex-col gap-4 p-4 ${rank === 1 ? "!border-[rgba(234,179,8,0.6)]" : ""}`}
    >
      <div className="flex items-center gap-3">
        {leader.avatarUrl ? (
          <img
            src={leader.avatarUrl}
            alt={leader.nickname}
            className="w-12 h-12 rounded-full object-cover"
          />
        ) : (
          <div className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-[#171717] text-lg font-bold">
            {leader.nickname.charAt(0).toUpperCase()}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-lg font-semibold text-white">
            {leader.nickname}
          </h3>
        </div>
        {rank != null && (
          <span
            className={`rounded-full border px-2 text-xs font-semibold leading-5 ${
              rank === 1
                ? "border-[#a16207] bg-[#2a1d05] text-[#facc15]"
                : "border-[#3730a3] bg-[#1e1b4b] text-[#a5b4fc]"
            }`}
          >
            #{rank}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 border-t border-white/10 pt-3 text-sm">
        <div>
          <p className="text-xs text-[#a3a3a3]">ROI (30D)</p>
          <p
            className={`font-semibold ${isFiniteNumber(leader.roi) && leader.roi < 0 ? "text-[#f87171]" : "text-[#4ade80]"}`}
          >
            {formatPercent(leader.roi)}
          </p>
        </div>
        <div>
          <p className="text-xs text-[#a3a3a3]">PnL</p>
          <p className="font-semibold text-white">
            ${formatNumber(leader.pnl)}
          </p>
        </div>
        <div>
          <p className="text-xs text-[#a3a3a3]">Win rate</p>
          <p className="font-semibold text-white">
            {formatPercent(leader.winRate)}
          </p>
        </div>
        <div>
          <p className="text-xs text-[#a3a3a3]">MDD</p>
          <p className="font-semibold text-white">
            {formatPercent(leader.mdd)}
          </p>
        </div>
      </div>

      <div className="flex gap-2 mt-auto">
        <button
          type="button"
          onClick={onViewDetails}
          className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-white/15 bg-[#171717] py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#262626]"
        >
          <ExternalLink className="w-4 h-4" />
          View Details
        </button>
        <button
          type="button"
          onClick={onSubscribe}
          disabled={isSubscribed}
          className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-accent py-2.5 text-sm font-semibold text-white hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50"
        >
          <UserPlus className="w-4 h-4" />
          {isSubscribed ? "Subscribed" : "Subscribe"}
        </button>
      </div>
    </div>
  );
};
